import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  Divider,
  Paper,
  IconButton,
  Collapse
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Refresh as RefreshIcon,
  RestartAlt as RestartIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AutoAwesome as GeneratingIcon
} from '@mui/icons-material';
import PropTypes from 'prop-types';

/**
 * Dialog component for displaying automated generation progress
 */
const GenerationProgressDialog = ({
  open,
  onClose,
  generationStatus = {
    status: 'idle',
    currentTask: null,
    completed: 0,
    total: 0,
    error: null,
    completedTasks: [],
    failedTasks: []
  },
  progressLog = [],
  progressPercentage = 0,
  onPause,
  onResume,
  onReset,
  onRestart,
  canStartGeneration = false,
  isGenerating = false,
  isPaused = false,
  isCompleted = false,
  hasError = false
}) => {
  const [showProgressLog, setShowProgressLog] = useState(false);

  // Format task name for display
  const formatTaskName = (taskId) => {
    if (!taskId) return '';
    return taskId.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Get status color and icon
  const getStatusDisplay = (status) => {
    switch (status) {
      case 'generating':
        return { color: 'primary', icon: <GeneratingIcon />, label: 'Generating' };
      case 'paused':
        return { color: 'warning', icon: <PauseIcon />, label: 'Paused' };
      case 'completed':
        return { color: 'success', icon: <CheckIcon />, label: 'Completed' };
      case 'error':
        return { color: 'error', icon: <ErrorIcon />, label: 'Error' };
      default:
        return { color: 'default', icon: <ScheduleIcon />, label: 'Idle' };
    }
  };

  const statusDisplay = getStatusDisplay(generationStatus?.status);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {statusDisplay.icon}
            <Typography variant="h6">
              Automated generation
            </Typography>
          </Box>
          <Chip
            label={statusDisplay.label}
            color={statusDisplay.color}
            size="small"
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Main Progress Section */}
        <Box sx={{ mb: 3 }}>
          {/* Progress Bar */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Progress
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {generationStatus?.completed || 0} of {generationStatus?.total || 0} tasks
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progressPercentage}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <Typography variant="h6" color="primary">
                {progressPercentage}%
              </Typography>
            </Box>
          </Box>

          {/* Current Task */}
          {generationStatus?.currentTask && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Currently generating: <strong>{formatTaskName(generationStatus.currentTask)}</strong>
              </Typography>
            </Alert>
          )}

          {/* Error Display */}
          {hasError && generationStatus?.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2">
                {generationStatus.error}
              </Typography>
            </Alert>
          )}

          {/* Completion Summary */}
          {isCompleted && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2">
                {generationStatus?.summary || (
                  <>
                    Generation completed successfully!
                    {generationStatus?.completedTasks?.length > 0 && (
                      <> Generated {generationStatus?.completedTasks?.length} tasks.</>
                    )}
                    {generationStatus?.failedTasks?.length > 0 && (
                      <> {generationStatus?.failedTasks?.length} tasks failed.</>
                    )}
                  </>
                )}
              </Typography>
            </Alert>
          )}
        </Box>

        {/* Control Buttons */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          {isGenerating && (
            <Button
              variant="outlined"
              startIcon={<PauseIcon />}
              onClick={onPause}
              color="warning"
            >
              Pause
            </Button>
          )}

          {isPaused && (
            <Button
              variant="contained"
              startIcon={<PlayIcon />}
              onClick={onResume}
              color="success"
            >
              Resume
            </Button>
          )}

          {(hasError || isCompleted || isPaused) && (
            <>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={onReset}
                color="primary"
              >
                Reset
              </Button>
              {onRestart && (
                <Button
                  variant="outlined"
                  startIcon={<RestartIcon />}
                  onClick={() => onRestart()}
                  color="secondary"
                >
                  Restart from Beginning
                </Button>
              )}
            </>
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Progress Log Section */}
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={showProgressLog ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setShowProgressLog(!showProgressLog)}
            sx={{ mb: 1 }}
          >
            Progress log ({progressLog.length} entries)
          </Button>

          <Collapse in={showProgressLog}>
            <Paper
              variant="outlined"
              sx={{
                maxHeight: 200,
                margin: '0px !important',
                overflow: 'auto',
                bgcolor: 'grey.50',
                '&::-webkit-scrollbar': {
                  width: '8px'
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  borderRadius: '4px'
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.3)'
                  }
                }

              }}
            >
              {progressLog.length > 0 ? (
                <List dense>
                  {progressLog.slice().reverse().map((entry, index) => {
                    const severity = entry.type === 'error' ? 'error' :
                      entry.type === 'warning' ? 'warning' :
                        entry.type === 'success' ? 'success' : 'info';

                    return (
                      <ListItem key={index}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {entry.type === 'error' && <ErrorIcon color="error" fontSize="small" />}
                          {entry.type === 'success' && <CheckIcon color="success" fontSize="small" />}
                          {entry.type === 'warning' && <PauseIcon color="warning" fontSize="small" />}
                          {entry.type === 'generating' && <GeneratingIcon color="primary" fontSize="small" />}
                          {entry.type === 'info' && <ScheduleIcon color="action" fontSize="small" />}
                        </ListItemIcon>
                        <ListItemText
                          primary={entry.message}
                          secondary={new Date(entry.timestamp).toLocaleTimeString()}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              ) : (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No progress log entries yet
                  </Typography>
                </Box>
              )}
            </Paper>
          </Collapse>
        </Box>

        {/* Task Dependencies Section Removed */}
      </DialogContent>

      <DialogActions sx={{px: 5, pb: 4}}>
        <Button onClick={onClose} 
        sx={{
          minWidth: 'auto',
          height: 36,
          minHeight: 36
        }} 
        variant='outlined'>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

GenerationProgressDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  generationStatus: PropTypes.object,
  progressLog: PropTypes.array,
  progressPercentage: PropTypes.number,
  onPause: PropTypes.func,
  onResume: PropTypes.func,
  onReset: PropTypes.func,
  onRestart: PropTypes.func,
  canStartGeneration: PropTypes.bool,
  isGenerating: PropTypes.bool,
  isPaused: PropTypes.bool,
  isCompleted: PropTypes.bool,
  hasError: PropTypes.bool
};

GenerationProgressDialog.defaultProps = {
  generationStatus: {
    status: 'idle',
    currentTask: null,
    completed: 0,
    total: 0,
    error: null,
    completedTasks: [],
    failedTasks: []
  },
  progressLog: [],
  progressPercentage: 0,
  canStartGeneration: false,
  isGenerating: false,
  isPaused: false,
  isCompleted: false,
  hasError: false
};

export default GenerationProgressDialog;